import { useEffect } from 'react';
import { useBoolean } from 'usehooks-ts';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { useDrawerOpenTracker } from '@/context';

import { useGetMinorDetailAndRequiredDocuments } from '@/hooks/minor/useGetminorDetailAndRequiredDocuments';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { setOperationData } from '@/redux/operation';
import { selectSigningDocuments } from '@/redux/operation/selectors';
import { selectUserData } from '@/redux/user-data';

import { MinorDocumentSigningDrawer } from '../drawers/MinorDocumentSigningDrawer';

type MinorDocumentSigningProps = {
  disabled?: boolean;
};

export const MinorDocumentSigning = ({
  disabled = false,
}: MinorDocumentSigningProps) => {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { formsToSign, documentsSigningStatus, remainingDocsToSign } =
    useAppSelector(selectSigningDocuments);
  const { accountTypes } = useAppSelector(selectUserData);

  const {
    value: isDrawerVisible,
    setTrue: showDrawer,
    setFalse: hideDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isDrawerVisible);

  const { data, refetch } = useGetMinorDetailAndRequiredDocuments();

  const openDrawer = () => {
    showDrawer();
    refetch();
  };

  useEffect(() => {
    if (data) {
      dispatch(
        setOperationData({
          user: data.user || null,
          requiredSections: data.requiredSections || [],
          accountTypes: accountTypes || [],
        }),
      );
    }
  }, [data, dispatch, accountTypes]);

  return (
    <>
      <DocumentActionItem
        title={t('moreInfo.form.docSigning.title')}
        subDescription={
          remainingDocsToSign
            ? t('moreInfo.form.docSigning.subDescription', {
                count: remainingDocsToSign,
              })
            : ''
        }
        onClick={formsToSign.length ? openDrawer : undefined}
        status={documentsSigningStatus}
        hideAction={!formsToSign.length}
        disabled={disabled}
        enabledForAllStatuses
      />
      <MinorDocumentSigningDrawer open={isDrawerVisible} onClose={hideDrawer} />
    </>
  );
};
